import React, { useEffect, useRef, useState } from 'react';
import { ImageSchema } from '../Image/Image';

const maxScale = 143;
const minScale = 80;
const scaleStep = 10;

export interface GalleryImage extends ImageSchema {
  label?: string;
}

interface UseZoom {
  disableZoom?: boolean;
}

export const useZoom = ({ disableZoom }: UseZoom) => {
  const [zoom, setZoom] = useState(100);

  const previewRef = useRef<HTMLDivElement | null>(null);
  const previewContentRef = useRef<HTMLDivElement | null>(null);
  const containerOriginalHeightRef = useRef<number | null>(null);

  const handleWheelZoom = (event: React.WheelEvent) => {
    if (disableZoom) {
      return;
    }

    setZoom(prevZoom => {
      event.stopPropagation();

      const strenghtMultiplier = Math.abs(event.deltaY) > 40 ? 0.04 : 0.6;

      const delta = event.deltaY * -strenghtMultiplier;
      const newZoom = Math.min(Math.max(prevZoom + delta, minScale), maxScale);

      return newZoom;
    });
  };

  useEffect(() => {
    if (disableZoom) {
      return;
    }

    const handleZoom = () => {
      const preview = previewRef.current;
      const previewContent = previewContentRef.current;

      if (!preview || !previewContent) {
        return;
      }

      let containerOriginalHeight = containerOriginalHeightRef.current;

      if (!containerOriginalHeight) {
        containerOriginalHeightRef.current = previewContent.getBoundingClientRect().height;
        containerOriginalHeight = containerOriginalHeightRef.current;
      }

      const scale = zoom / 100;
      const { height: maxHeight } = preview.getBoundingClientRect();
      const newHeight = Math.min(containerOriginalHeight * scale, maxHeight);

      previewContent.style.height = `${newHeight}px`;
    };

    handleZoom();

    window.addEventListener('resize', handleZoom);
    return () => {
      window.removeEventListener('resize', handleZoom);
    };
  }, [zoom, disableZoom]);

  useEffect(() => {
    if (disableZoom) {
      return;
    }

    const handleZoomByKey = ({ key }: KeyboardEvent) => {
      if (key === '+' || key === '=') {
        setZoom(prevZoom => Math.min(prevZoom + scaleStep, maxScale));
      }

      if (key === '-') {
        setZoom(prevZoom => Math.max(prevZoom - scaleStep, minScale));
      }
    };

    window.addEventListener('keydown', handleZoomByKey);
    return () => {
      window.removeEventListener('keydown', handleZoomByKey);
    };
  }, [disableZoom]);

  return {
    zoom,
    setZoom,
    previewRef,
    previewContentRef,
    handlers: {
      onWheel: handleWheelZoom,
    },
  };
};
